import { useEffect, useState } from "react";

import { compareVersions } from "@bigbear/shared";

import { type CliDownloads, cliDownloadsFor, fetchLatestCliRelease } from "./api/updates";
import { latestRelease } from "./releases";

/** The last release published with no Linux command line (#958). A version
 * after it has one, so the committed release notes can link it before the
 * manifest answers; this one and older never will. */
const LAST_RELEASE_WITHOUT_CLI = "0.11.0";

export interface LatestCliDownload extends CliDownloads {
  /** As {@link LatestDownload}'s: the portal knows of a release whose files
   * are still on their way to the bucket. */
  readonly status: "ready" | "building";
}

const fallback: LatestCliDownload | null =
  latestRelease && compareVersions(latestRelease.version, LAST_RELEASE_WITHOUT_CLI) > 0
    ? { ...cliDownloadsFor(latestRelease.version), status: "ready" }
    : null;

/**
 * The newest Linux command line, the same way {@link useLatestDownload} finds
 * the newest installers: from the committed release notes at once, then from
 * the live manifest (`cli/latest.json`) once it answers, which is also what
 * brings the checksums. `null` until there is a release with one to offer.
 */
export function useLatestCliDownload(): LatestCliDownload | null {
  const [download, setDownload] = useState<LatestCliDownload | null>(fallback);

  useEffect(() => {
    const controller = new AbortController();

    fetchLatestCliRelease(controller.signal)
      .then((manifest) => {
        if (controller.signal.aborted) return;
        if (manifest && (!fallback || compareVersions(manifest.version, fallback.version) >= 0)) {
          setDownload({ ...manifest, status: "ready" });
        } else if (fallback) {
          // The release notes landed, and the release is still publishing:
          // an older version's manifest, or, before the first release with
          // a Linux build, none at all.
          setDownload({ ...fallback, status: "building" });
        }
      })
      .catch(() => {
        // Unreachable for now: the fallback, if any, is already showing.
      });

    return () => controller.abort();
  }, []);

  return download;
}
